"use client";

import { IconChevronRight } from "@tabler/icons-react";
import { useTranslations } from "next-intl";
import { Img } from "@/components/image";
import { DietCardBadges } from "@/features/cart/components/configure-diet/diet-card/diet-card-badges";
import { DietCardCateringLogo } from "@/features/cart/components/configure-diet/diet-card/diet-card-catering-logo";
import { DietCardDeliveryRow } from "@/features/cart/components/configure-diet/diet-card/diet-card-delivery-row";
import { DietCardMenuRow } from "@/features/cart/components/configure-diet/diet-card/diet-card-menu-row";
import {
  DietCardReviewsRow,
  hasDietReviewsRow,
} from "@/features/cart/components/configure-diet/diet-card/diet-card-reviews-row";
import { DietCateringInfoDialog } from "@/features/diets/components/diet-info-dialog/diet-catering-info-dialog";
import { stripHtml } from "@/features/diets/helpers/strip-html";
import type { DietCatalogDetail } from "@/features/diets/types";
import type { Globals } from "@/features/globals/types/globals.type";
import { Link } from "@/i18n/navigation";
import {
  formatCateringName,
  formatDietName,
} from "@/lib/helpers/format-brand-name";
import { cn } from "@/lib/utils";

const CATERING_LOGO_SIZE = 96;

export const DietCardDesktop = ({
  diet,
  globals,
  kcal,
  variant,
}: {
  diet: DietCatalogDetail;
  globals?: Globals | null;
  kcal?: string;
  variant?: string;
}) => {
  const t = useTranslations("diets");

  // Without the reviews row the card is shorter, so the square image shrinks
  // to keep the header and rows from being dwarfed by it.
  const showsReviews = hasDietReviewsRow(diet, globals);

  return (
    <div className="flex overflow-hidden rounded-lg border border-brand-grey2-300 bg-white">
      <div
        className={cn(
          "relative aspect-square shrink-0 self-start bg-brand-grey2",
          showsReviews ? "w-74" : "w-60",
        )}
      >
        {diet.imageUrl && (
          <Img
            alt={formatDietName(diet.name)}
            className="object-cover object-center"
            fill
            sizes={showsReviews ? "296px" : "240px"}
            src={diet.imageUrl}
          />
        )}
      </div>
      <div className="flex min-w-0 flex-1 flex-col">
        {/* The dialog trigger covers the whole header, so the catering link can
        sit on top of it instead of being nested inside a button. */}
        <div className="relative flex items-center gap-4 bg-brand-grey1-100 p-4 text-left transition-colors hover:bg-brand-grey1-300">
          <DietCateringInfoDialog
            diet={diet}
            trigger={
              <button className="absolute inset-0 cursor-pointer" type="button">
                <span className="sr-only">{t("info.title")}</span>
              </button>
            }
          />
          <DietCardCateringLogo
            imageUrl={diet.catering.logo.url}
            name={formatCateringName(diet.catering.name)}
            size={CATERING_LOGO_SIZE}
          />
          <div className="flex min-w-0 flex-1 flex-col gap-0">
            <DietCardBadges diet={diet} />
            <div className="mt-2 truncate font-medium text-lg">
              <span data-pw="diet-header">{formatDietName(diet.name)}</span>
              <span aria-hidden="true" className="mx-2 text-brand-green2-300">
                &bull;
              </span>
              <Link
                className="relative z-10 hover:underline"
                href={{
                  pathname: "/caterings/[slug]",
                  params: { slug: diet.catering.slug },
                }}
              >
                {formatCateringName(diet.catering.name)}
              </Link>
            </div>
            {diet.description && (
              <div className="truncate text-brand-green2-300 text-sm">
                {stripHtml(diet.description)}
              </div>
            )}
          </div>
          <IconChevronRight
            className="size-6 shrink-0 text-brand-green1-300"
            stroke={1.5}
          />
        </div>
        <DietCardReviewsRow diet={diet} globals={globals} />
        <DietCardDeliveryRow diet={diet} />
        <DietCardMenuRow dietId={diet.id} kcal={kcal} variant={variant} />
      </div>
    </div>
  );
};
